import { AppData, Utils } from '@lightningjs/sdk';
import { constants } from 'aliases';
import { Platform } from 'models/platforms/platform';
import { OneTrustSdk } from 'types/oneTrust';
// @ts-ignore
import processEnv from 'processEnv';

const LAYER_STYLE_ID = 'oneTrustLayer';

/**
 * The Lightning CLI's generated `startApp.js` styles the app canvas as
 * `z-index: 2`, and OneTrust renders its full-screen `.ot-DialogBox` at
 * `z-index: 1`, so the preference center comes up behind the app. `startApp.js`
 * is CLI boilerplate we don't own, so lift OneTrust's dialog above it instead.
 */
const DIALOG_Z_INDEX = 3;

export const ensureOneTrustAboveCanvas = (): void => {
  if (document.getElementById(LAYER_STYLE_ID)) return;

  const style = document.createElement('style');
  style.id = LAYER_STYLE_ID;
  style.type = 'text/css';
  style.innerHTML = `.ot-DialogBox { z-index: ${DIALOG_Z_INDEX} !important; }`;

  document.head.appendChild(style);
};

/**
 * Browser dev only. `unknown` has no OneTrust package of its own, so it borrows
 * one of the TV copies. `APP_ONETRUST_UNKNOWN_SDK` picks which (`tizen` or
 * `lg`); anything else falls back to the Tizen copy
 */
const getUnknownSdkPath = (): string => {
  const choice = processEnv['APP_ONETRUST_UNKNOWN_SDK'];

  if (choice === Platform.LG) return constants.oneTrust.webOsSdkPath;

  if (!!choice && choice !== Platform.TIZEN) {
    AppData?.log.debug(
      `[OneTrust] unrecognized APP_ONETRUST_UNKNOWN_SDK "${choice}", using tizen`,
    );
  }

  return constants.oneTrust.tizenSdkPath;
};

/**
 * OneTrust ships a separate `onetrust.js` package per platform, so a platform
 * with no package returns `null` and the caller bails out.
 *
 * Vizio and Comcast stay unsupported even when `APP_USE_ONETRUST_OVERRIDE` is
 * set, so a stray override on real hardware is a no-op rather than a live load.
 * `unknown` is the exception.
 */
export const getOneTrustSdkPath = (platform: Platform): string | null => {
  switch (platform) {
    case Platform.TIZEN:
      return constants.oneTrust.tizenSdkPath;
    case Platform.LG:
      return constants.oneTrust.webOsSdkPath;
    case Platform.UNKNOWN:
      return getUnknownSdkPath();
    default:
      return null;
  }
};

export const loadOneTrustScript = (path: string): Promise<OneTrustSdk> => {
  return new Promise<OneTrustSdk>((resolve, reject) => {
    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.src = Utils.asset(path);
    script.onload = () => {
      const sdk = window.OneTrust;

      if (!sdk) {
        reject(new Error('onetrust.js loaded without defining window.OneTrust'));
        return;
      }

      resolve(sdk);
    };
    script.onerror = e => {
      reject(e);
    };
    document.head.appendChild(script);
  });
};
